import { terminalXtermOptions } from './terminalTheme';
import { loadTerminalAddons } from './terminalAddons';

/**
 * Create an xterm + fit addon inside `container`, with the Warp-style block
 * dividers every local terminal draws. Moved out of `useEmbeddedTerminal`
 * so the hook can stay about sessions.
 *
 * Warp-style block dividers: shells emit OSC 133;A at each prompt start
 * (terminal.rs prompt configs); we render a real DOM divider line there via
 * the decorations API — skipping the first prompt of the session. All
 * dividers are tracked so a screen clear can dispose them (the decorations
 * are DOM elements pinned to buffer markers — cls/clear wipes the text but
 * would leave the lines floating otherwise).
 *
 * Returns `{ term, fitAddon, clearDividers, addons }` (addons: terminalAddons.js).
 */
export async function createTerminalXterm(container, fontSize, isDark = true, optionAsMeta = false) {
  const [{ Terminal }, { FitAddon }] = await Promise.all([
    import('@xterm/xterm'),
    import('@xterm/addon-fit'),
  ]);
  await import('@xterm/xterm/css/xterm.css');
  if (!container) return null;

  // The terminal's own light/dark (useTerminalIsDark), not the page's: a
  // terminal can be dark in the light app theme and the other way round.
  const term = new Terminal(terminalXtermOptions(isDark, fontSize, optionAsMeta));

  const promptSeen = { current: false };
  const dividers = [];
  term.parser.registerOscHandler(133, (data) => {
    if (typeof data === 'string' && data.startsWith('A')) {
      if (promptSeen.current) {
        const marker = term.registerMarker(0);
        if (marker) {
          const deco = term.registerDecoration({ marker });
          deco?.onRender((el) => {
            if (!el.dataset.apbDivider) {
              el.dataset.apbDivider = '1';
              el.classList.add('term-block-divider');
            }
          });
          dividers.push({ marker, deco });
        }
      } else {
        promptSeen.current = true;
      }
    }
    return true;
  });

  const clearDividers = () => {
    for (const d of dividers) {
      d.deco?.dispose?.();
      d.marker?.dispose?.();
    }
    dividers.length = 0;
    promptSeen.current = false;
  };

  const fitAddon = new FitAddon();
  term.loadAddon(fitAddon);
  term.open(container);
  const addons = await loadTerminalAddons(term);
  fitAddon.fit();
  return { term, fitAddon, clearDividers, addons };
}
